import { DateText, DisciplineTag, StatusChip } from '@batiflow/ui';
import { Button, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { Edit20Regular, Open20Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '@batiflow/i18n';
import { useLocale } from '@/app/providers/i18n-provider';
import { DocumentEditForm } from './document-edit-form';
import { canEditDocument, type DocumentDetail } from './document-model';
import { phaseOptionLabel } from './metadata-fields';
import { usePanelStyles } from './panel-styles';

/** The address of the viewer of a document (the `href` of "Open", so it works in a new tab too). */
export const viewerPath = (orgId: string, projectId: string, documentId: string): string =>
  `/o/${orgId}/projects/${projectId}/documents/${documentId}`;

const STATUS_INTENT = {
  uploading: 'informative',
  scanning: 'informative',
  available: 'success',
  rejected: 'danger',
} as const;

export interface DocumentDetailsProps {
  /** The viewer is part of the organisation's project: null orgId or projectId hides "Open". */
  orgId: string | null;
  projectId: string | null;
  spaceId: string;
  document: DocumentDetail;
}

/**
 * The details of a document: code (read-only), title and metadata with an "Edit" form, what is
 * missing for a code, the author organisation, the linked deliverables and the link to the viewer.
 */
export function DocumentDetails({ orgId, projectId, spaceId, document }: DocumentDetailsProps) {
  const { t } = useTranslation('documents');
  const styles = usePanelStyles();
  const navigate = useNavigate();
  const locale = useLocale();
  const [editing, setEditing] = useState(false);

  const viewer = orgId !== null && projectId !== null ? { orgId, projectId } : null;
  const href = viewer ? viewerPath(viewer.orgId, viewer.projectId, document.id) : null;
  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!viewer) return;
    // A plain click moves inside the app; a modified click keeps the browser's own behaviour.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    void navigate({
      to: '/o/$orgId/projects/$projectId/documents/$documentId',
      params: { ...viewer, documentId: document.id },
    });
  };

  const missing = new Intl.ListFormat(intlLocale(locale), {
    style: 'long',
    type: 'conjunction',
  }).format(document.missingMetadata.map((field) => t(`metadata.fields.${field}`)));
  const none = <span className={styles.muted}>{t('list.none')}</span>;
  const status = document.latestVersionStatus;

  return (
    <section className={styles.section} aria-label={t('panel.detailsRegion')}>
      <div className={styles.sectionHead}>
        <h3 className={styles.heading}>{t('panel.details')}</h3>
        <div className={styles.actions}>
          {href ? (
            <Button
              as="a"
              size="small"
              appearance="primary"
              icon={<Open20Regular />}
              href={href}
              onClick={open}
            >
              {t('panel.open')}
            </Button>
          ) : null}
          {canEditDocument(document) && !editing ? (
            <Button
              size="small"
              icon={<Edit20Regular />}
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('panel.edit')}
            </Button>
          ) : null}
        </div>
      </div>

      {document.lockedByFlowId ? (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>{t('panel.locked')}</MessageBarBody>
        </MessageBar>
      ) : null}
      {document.missingMetadata.length > 0 ? (
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>{t('panel.missingMetadata', { fields: missing })}</MessageBarBody>
        </MessageBar>
      ) : null}

      {editing ? (
        <DocumentEditForm
          spaceId={spaceId}
          document={document}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : (
        <dl className={styles.facts}>
          <dt className={styles.factLabel}>{t('list.columns.code')}</dt>
          <dd className={styles.factValue}>
            {document.code ? (
              <bdi dir="ltr" className={styles.code}>
                {document.code}
              </bdi>
            ) : (
              none
            )}
          </dd>
          <dt className={styles.factLabel}>{t('list.columns.title')}</dt>
          <dd className={styles.factValue}>
            <bdi>{document.title}</bdi>
          </dd>
          <dt className={styles.factLabel}>{t('list.columns.index')}</dt>
          <dd className={styles.factValue}>
            {document.revisionIndex ? <bdi dir="ltr">{document.revisionIndex}</bdi> : none}
          </dd>
          <dt className={styles.factLabel}>{t('metadata.discipline')}</dt>
          <dd className={styles.factValue}>
            {document.discipline ? <DisciplineTag discipline={document.discipline} /> : none}
          </dd>
          <dt className={styles.factLabel}>{t('metadata.phase')}</dt>
          <dd className={styles.factValue}>
            {document.phaseType ? phaseOptionLabel(t, document.phaseType) : none}
          </dd>
          <dt className={styles.factLabel}>{t('metadata.docType')}</dt>
          <dd className={styles.factValue}>
            {document.docType ? t(`docTypes.${document.docType}`) : none}
          </dd>
          <dt className={styles.factLabel}>{t('metadata.zone')}</dt>
          <dd className={styles.factValue}>{document.zone ? <bdi>{document.zone}</bdi> : none}</dd>
          <dt className={styles.factLabel}>{t('metadata.scale')}</dt>
          <dd className={styles.factValue}>
            {document.scale ? <bdi dir="ltr">{document.scale}</bdi> : none}
          </dd>
          <dt className={styles.factLabel}>{t('list.columns.status')}</dt>
          <dd className={styles.factValue}>
            {status ? (
              <StatusChip label={t(`status.${status}`)} intent={STATUS_INTENT[status]} />
            ) : (
              none
            )}
          </dd>
          <dt className={styles.factLabel}>{t('list.columns.author')}</dt>
          <dd className={styles.factValue}>
            {document.ownerOrgName ? <bdi>{document.ownerOrgName}</bdi> : t('list.partner')}
          </dd>
          <dt className={styles.factLabel}>{t('list.columns.modified')}</dt>
          <dd className={styles.factValue}>
            <DateText value={document.updatedAt} variant="dateTime" />
          </dd>
          <dt className={styles.factLabel}>{t('panel.deliverables')}</dt>
          <dd className={styles.factValue}>
            {document.deliverables.length > 0
              ? document.deliverables.map((deliverable, position) => (
                  <span key={deliverable.id}>
                    {position > 0 ? ', ' : ''}
                    <bdi>{deliverable.title}</bdi>
                  </span>
                ))
              : t('panel.noDeliverables')}
          </dd>
        </dl>
      )}
    </section>
  );
}
